import { getZonedParts, zonedTimeToEpochMs } from "@if-tracking/shared";

/** Human-readable date, e.g. "Sun, Jun 23, 2024", in the given timezone. */
export function formatDate(epochMs: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(epochMs));
}

/** Human-readable time, e.g. "9:52 PM", in the given timezone. */
export function formatTime(epochMs: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  }).format(new Date(epochMs));
}

/** Compact "Jun 23" style label for a YYYY-MM-DD date key (no timezone shift). */
export function formatMonthDay(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric"
  }).format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)));
}

/** Short weekday, e.g. "Mon", for a YYYY-MM-DD date key. */
export function formatWeekday(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short"
  }).format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)));
}

/** Compact duration, e.g. "18h 30m" or "2d 4h 10m". */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(" ");
}

export function formatHours(seconds: number): string {
  return (seconds / 3600).toFixed(2);
}

/** Two-digit zero pad. */
function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * Convert an epoch-ms instant to the `YYYY-MM-DDTHH:mm` string a
 * `<input type="datetime-local">` expects, expressed in `timeZone`.
 */
export function epochToInputValue(epochMs: number, timeZone: string): string {
  const p = getZonedParts(epochMs, timeZone);
  return `${String(p.year).padStart(4, "0")}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * Inverse of `epochToInputValue`: interpret the local datetime string as a
 * wall-clock time in `timeZone` and return the corresponding epoch ms.
 * Returns null if the string is empty or malformed.
 */
export function inputValueToEpoch(value: string, timeZone: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value.trim());
  if (!match) {
    return null;
  }
  return zonedTimeToEpochMs(
    {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4]),
      minute: Number(match[5])
    },
    timeZone
  );
}
